import React, { Component } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import { enUS, fr, de } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "@fortawesome/fontawesome-free/css/all.css";
import moment from "moment";
import I18nProvider from "../../../../services/I18nProvider";
import { Input } from "reactstrap";
import dateFormatService from "../../../../services/dateFormatService";
import { birthDateFormatter } from "../../../../utils/utils";

const intl = I18nProvider;

registerLocale("en-US", enUS);
registerLocale("fr", fr);
registerLocale("de", de);

// Helper function to check if this observation is a date of birth
// Uses the centralized service method for consistency
const isDOBObservation = (attributeValue) => {
  return dateFormatService.isDOBAttribute(attributeValue);
};

class DateObservation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }
  static getDisplayValue(attributeValue, valuePlace, forInput = false) {
    if (
      attributeValue.valueObservation &&
      attributeValue.valueObservation.value
    ) {
      let valueDate = null;
      if (valuePlace === "from") {
        valueDate = attributeValue.valueObservation.value.valueFromDate;
      } else if (valuePlace === "to") {
        valueDate = attributeValue.valueObservation.value.valueToDate;
      } else {
        valueDate = attributeValue.valueObservation.value.valueDate;
      }

      if (valueDate == null) {
        return "";
      }

      // For HTML5 date inputs, return YYYY-MM-DD format
      if (forInput) {
        return moment(valueDate).format("YYYY-MM-DD");
      }

      // Check if this is a DOB observation - use birthDateFormatter
      if (isDOBObservation(attributeValue)) {
        return birthDateFormatter(valueDate);
      }

      // For all other observation dates, use centralized date formatting service
      return dateFormatService.formatDate(valueDate);
    }
  }

  static getPatientProfileDisplayValue(attributeValue, locale = fr) {
    if (
      attributeValue.valueObservation &&
      attributeValue.valueObservation.value
    ) {
      let valueDate = attributeValue.valueObservation.value.valueDate;

      if (valueDate != null) {
        // Check if this is a DOB observation - use birthDateFormatter
        if (isDOBObservation(attributeValue)) {
          return birthDateFormatter(valueDate);
        }

        // For all other observation dates, use centralized date formatting service
        return dateFormatService.formatDate(valueDate);
      } else {
        let valueString = attributeValue.valueObservation.value.valueString;
        if (valueString) {
          // Check if this is a DOB observation - use birthDateFormatter
          if (isDOBObservation(attributeValue)) {
            return birthDateFormatter(valueString);
          }

          // For all other observation dates, try to parse and format
          return dateFormatService.formatAttributeDateValue(valueString);
        }
      }
    }
  }

  static getValueForSubmission(attributeValue) {
    if (
      attributeValue.valueObservation &&
      attributeValue.valueObservation.value &&
      attributeValue.valueObservation.value.valueDate
    ) {
      attributeValue.valueObservation.value.valueDate = new Date(
        attributeValue.valueObservation.value.valueDate
      ).getTime();
    }
    return attributeValue;
  }

  render() {
    let {
      attributeValue,
      setData,
      observationCode,
      locale = "fr", //TODO: pass locale from parent
      useNativeDateInput = true, // Feature flag - set to true to test native input
    } = this.props;
    locale = intl.getLocale();
    locale = locale.slice(0, 2);

    attributeValue.valueObservation = attributeValue.valueObservation || {};
    attributeValue.valueObservation.value =
      attributeValue.valueObservation.value || {};
    let obsVal = attributeValue.valueObservation.value.valueDate
      ? new Date(attributeValue.valueObservation.value.valueDate)
      : null;
    attributeValue.valueObservation.code = observationCode;

    // Native HTML5 date input implementation
    if (useNativeDateInput) {
      return (
        <div className="inputHolder flex-row">
          <Input
            className="ui-attribute-el"
            type="date"
            defaultValue={obsVal ? moment(obsVal).format("YYYY-MM-DD") : ""}
            onChange={(e) => {
              const dateValue = e.target.value;
              if (!dateValue) {
                // Clear the date but keep the structure
                attributeValue.valueObservation.value = {
                  valueDate: null,
                  valueString: null,
                  valueAsString: null,
                };
                attributeValue.valueObservation.observationDateStr = null;
                attributeValue.valueObservation.observationDate = null;
                setData(attributeValue, null);
                return;
              }

              // Convert YYYY-MM-DD to date and store
              const date = new Date(dateValue);
              const formattedDate = dateValue; // Keep as YYYY-MM-DD
              attributeValue.valueObservation.value.valueDate = formattedDate;
              attributeValue.valueObservation.value.valueString = null;
              attributeValue.valueObservation.value.valueAsString = null;
              setData(attributeValue, formattedDate);
            }}
            onBlur={this.props.onBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && this.props.onEnterKey) {
                e.preventDefault();
                this.props.onEnterKey();
              }
            }}
            disabled={this.props.disabled}
          />
        </div>
      );
    }

    // Original react-datepicker implementation (fallback)
    return (
      <div className="inputHolder flex-row">
        <div
          className="date-picker-wrapper"
          style={{ position: "relative", display: "inline-block" }}
        >
          <DatePicker
            className="ui-attribute-el"
            selected={obsVal}
            onChange={(date) => {
              if (!date) {
                // Clear the date but keep the structure
                attributeValue.valueObservation.value = {
                  valueDate: null,
                  valueString: null,
                  valueAsString: null,
                };
                // Also clear the timestamp fields
                attributeValue.valueObservation.observationDateStr = null;
                attributeValue.valueObservation.observationDate = null;
                setData(attributeValue, null);
                return;
              }

              const formattedDate = format(date, "yyyy-MM-dd");
              attributeValue.valueObservation.value.valueDate = formattedDate;
              attributeValue.valueObservation.value.valueString = null;
              attributeValue.valueObservation.value.valueAsString = null;
              setData(attributeValue, formattedDate);
            }}
            onBlur={this.props.onBlur}
            dateFormat="dd/MM/yyyy"
            locale={locale}
            placeholderText="dd/mm/yyyy"
            shouldCloseOnSelect={true}
            preventOpenOnFocus={true}
            onChangeRaw={(e) => {
              // Format input while typing: auto-add slashes for dd/mm/yyyy format
              let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
              if (value.length === 2) {
                value = value + '/';
              } else if (value.length === 3) {
                value = value.substring(0, 2) + '/' + value.substring(2);
              } else if (value.length === 4) {
                value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/';
              } else if (value.length >= 5) {
                value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/' + value.substring(4, 8);
              }
              e.target.value = value;
            }}
            disabled={this.props.disabled}
          />
          <span
            className="fa fa-calendar"
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: "#aaa",
            }}
          />
        </div>
      </div>
    );
  }
}

export default DateObservation;
